import React from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {IGamifiable} from '../../../../incase-domain/elements/base/gamifiable';
import {selectedPresentationMetaSelector} from '../../../../store/selectors/presentation.selectors';
import SectionTitle from '../../../elements/common/SectionTitle';
import DebouncedNumberInput from './debounced/DebounceNumberInput';
import Label from '../../../../incase-domain/components/label/Label';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';

type GamificationTitleKey = 'presEditPage.gamification.correctAnswer' | 'presEditPage.gamification.incorrectAnswer';
type GamificationInfoKey = 'presEditPage.gamification.correctAnswerDescription' | 'presEditPage.gamification.incorrectAnswerDescription';

interface Element {
    title: GamificationTitleKey;
    info: GamificationInfoKey;
    onChange: (value: number) => void;
    point: number;
}

export function ElementGamificationOptions(): JSX.Element {
    const highlightedElement = useHighlightedElement() as IGamifiable;
    const updateHighlightedElement = useSetHighlightedElement();

    const {t} = useTranslation();
    const {
        gameConfig: {correctPointsPlus = 0, wrongPointsMinus = 0},
    } = useSelector(selectedPresentationMetaSelector);

    const elements: Element[] = [
        {
            title: 'presEditPage.gamification.correctAnswer',
            info: 'presEditPage.gamification.correctAnswerDescription',
            onChange: (value: number) => {
                updateHighlightedElement.single('gameConfig', {
                    ...highlightedElement.gameConfig,
                    correctPointsPlus: value,
                });
            },
            point: highlightedElement.gameConfig?.correctPointsPlus || correctPointsPlus,
        },
        {
            title: 'presEditPage.gamification.incorrectAnswer',
            info: 'presEditPage.gamification.incorrectAnswerDescription',
            onChange: (value: number) => {
                updateHighlightedElement.single('gameConfig', {
                    ...highlightedElement.gameConfig,
                    wrongPointsMinus: value,
                });
            },
            point: highlightedElement.gameConfig?.wrongPointsMinus || wrongPointsMinus,
        },
    ];

    return (
        <div className="l-group--form u-mb-3">
            <div className="gamification">
                <SectionTitle label={t('presEditPage.gamification.title')} infoText={t('designSidepanel.gamificationDescription')} />
                <div className="u-flex u-flex-col u-gap-1">
                    {elements.map((element) => (
                        <div className="u-flex u-v-center u-h-between u-gap-2">
                            <Label>{t(element.title)}</Label>
                            <DebouncedNumberInput value={element.point} onChange={element.onChange} min={0} step={1} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
